<template>
  <div>
  <woot-modal
    :show.sync="show"
    :on-close="onClose"
    :show-close-button="false"
    class="schedule-demo-modal-wrapper"
    size="modal-big"
  >
    <div class="schedule-demo-modal">
      <!-- Header: title + tabs (same row, labels list–style) + close -->
      <div class="modal-header">
        <div class="modal-header-left">
          <h2 class="header-title">
            {{ scheduleModalTitle }}
          </h2>
          <div
            v-if="showBookingTypeTabs"
            class="schedule-modal-tabs"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              :aria-selected="activeTab === 'kivo'"
              class="tab-button"
              :class="{ active: activeTab === 'kivo' }"
              @click="activeTab = 'kivo'"
            >
              {{ $t('SCHEDULE_DEMO.TAB_KIVO') }}
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="activeTab === 'manual'"
              class="tab-button"
              :class="{ active: activeTab === 'manual' }"
              @click="activeTab = 'manual'"
            >
              {{ $t('SCHEDULE_DEMO.TAB_MANUAL') }}
            </button>
          </div>
        </div>
        <div class="header-actions">
          <button class="close-btn" @click="handleClose">
            <fluent-icon icon="dismiss" size="20" />
          </button>
        </div>
      </div>

      <div class="modal-tab-panels">
        <!-- Kivo Calendar (unchanged flow; only wrapped in a tab panel) -->
        <div
          v-show="activeTab === 'kivo'"
          class="tab-panel tab-panel--kivo"
        >
          <!-- Loading State -->
          <div v-if="isInitializing" class="loading-state">
            <spinner size="large" />
            <p class="loading-text">{{ $t('SCHEDULE_DEMO.LOADING') }}</p>
          </div>

          <!-- No Event Types State -->
          <div v-else-if="eventTypes.length === 0" class="no-events-state">
            <fluent-icon icon="calendar" size="48" class="empty-icon" />
            <p class="empty-text">{{ $t('SCHEDULE_DEMO.NO_EVENT_TYPES') }}</p>
            <div class="empty-actions">
              <woot-button @click="goToProfileSettings">
                {{ $t('SCHEDULE_DEMO.ADD_EVENT_TYPE') }}
              </woot-button>
              <woot-button
                v-if="showSwitchToReminder"
                variant="smooth"
                color-scheme="secondary"
                @click="switchToReminder"
              >
                {{ $t('SCHEDULE_DEMO.SET_REMINDER_INSTEAD') }}
              </woot-button>
            </div>
          </div>

          <!-- BOOKING FORM VIEW - Shows when a slot is selected -->
          <div v-else-if="selectedSlot" class="booking-view">
            <!-- Left: Meeting Summary -->
            <div class="booking-summary-panel">
              <h3 class="summary-event-name">{{ selectedEventType?.name }}</h3>

              <div class="summary-info-card">
                <div class="info-row">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="clock-icon"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{{ selectedEventType?.duration }} min</span>
                </div>
              </div>

              <div class="summary-info-card">
                <div class="info-label">Timezone:</div>
                <div class="info-value">
                  {{ selectedTimezone.split('/')[1] || selectedTimezone }}
                </div>
              </div>

              <div class="summary-info-card">
                <div class="info-label">Selected time interval:</div>
                <div class="info-value">
                  ({{ formatTime(selectedSlot) }}-{{
                    formatEndTime(selectedSlot)
                  }})
                </div>
                <div class="info-value">{{ selectedDateDisplayFull }}</div>
              </div>
            </div>

            <!-- Right: Booking Form -->
            <div class="booking-form-panel">
              <div class="form-group">
                <label class="form-label">Subject</label>
                <input
                  v-model="bookingSubject"
                  type="text"
                  class="form-input"
                  placeholder="Enter meeting subject"
                />
              </div>

              <div class="form-group">
                <label class="form-label">{{
                  $t('SCHEDULE_DEMO.SELECT_ACCOUNT_MANAGER_LABEL')
                }}</label>
                <div class="multiselect-wrap">
                  <multiselect
                    v-model="selectedAccountManagerUser"
                    :options="meetingRoleAgentOptions"
                    track-by="id"
                    label="name"
                    :placeholder="
                      $t('SCHEDULE_DEMO.SELECT_ACCOUNT_MANAGER_PLACEHOLDER')
                    "
                    :allow-empty="true"
                    :close-on-select="true"
                    select-label=""
                    deselect-label=""
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{
                  $t('SCHEDULE_DEMO.SELECT_LEAD_LABEL')
                }}</label>
                <div class="multiselect-wrap">
                  <multiselect
                    v-model="selectedLeadUser"
                    :options="meetingRoleAgentOptions"
                    track-by="id"
                    label="name"
                    :placeholder="$t('SCHEDULE_DEMO.SELECT_LEAD_PLACEHOLDER')"
                    :allow-empty="true"
                    :close-on-select="true"
                    select-label=""
                    deselect-label=""
                  />
                </div>
              </div>

              <div v-if="showPlatformSelector" class="form-group">
                <label class="form-label">{{
                  $t('SCHEDULE_DEMO.PLATFORM_LABEL')
                }}</label>
                <div
                  v-if="isLoadingEventDetails"
                  class="platform-selector-loading"
                >
                  <spinner size="small" />
                </div>
                <div v-else class="platform-selector" role="radiogroup">
                  <button
                    v-for="option in meetingPlatformOptions"
                    :key="option.id"
                    type="button"
                    role="radio"
                    class="platform-option"
                    :class="{ selected: selectedPlatform === option.id }"
                    :aria-checked="selectedPlatform === option.id"
                    @click="selectedPlatform = option.id"
                  >
                    <span class="platform-option__icon" aria-hidden="true">
                      <meeting-platform-icon
                        :platform-id="option.id"
                        :alt="option.label"
                      />
                    </span>
                    <span class="platform-option__label">{{
                      option.label
                    }}</span>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Add guest's email</label>
                <input
                  v-model="guestEmail"
                  type="email"
                  :class="['form-input', { 'input-error': guestEmailError }]"
                  placeholder="Add guest's email"
                  @keyup.enter="addGuest"
                  @input="clearGuestEmailError"
                />
                <span v-if="guestEmailError" class="error-message">{{
                  guestEmailError
                }}</span>
                <button
                  class="add-guest-btn"
                  type="button"
                  @click="addGuest"
                >
                  Add guests
                </button>
                <div v-if="guests.length > 0" class="guests-list">
                  <div
                    v-for="(guest, idx) in guests"
                    :key="idx"
                    class="guest-tag"
                  >
                    <span>{{ guest }}</span>
                    <button
                      class="remove-guest"
                      type="button"
                      @click="removeGuest(idx)"
                    >
                      <fluent-icon icon="dismiss" size="12" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <woot-button
                  variant="smooth"
                  color-scheme="secondary"
                  @click="clearSlotSelection"
                >
                  Back
                </woot-button>
                <woot-button
                  :is-loading="isBooking"
                  color-scheme="primary"
                  @click="bookDemo"
                >
                  {{
                    isRescheduleMode
                      ? $t('SCHEDULE_DEMO.CONFIRM_RESCHEDULE')
                      : $t('SCHEDULE_DEMO.CONFIRM_BOOKING')
                  }}
                </woot-button>
              </div>
            </div>
          </div>

          <!-- CALENDAR VIEW - Shows when no slot is selected -->
          <div v-else class="schedule-content">
            <!-- LEFT PANEL: Event Types -->
            <div class="event-types-panel">
              <div
                v-for="eventType in eventTypes"
                :key="eventType.id"
                :class="[
                  'event-type-card',
                  { selected: selectedEventType?.id === eventType.id },
                ]"
                @click="selectEventType(eventType)"
              >
                <h4 class="event-name">{{ eventType.name }}</h4>
                <div class="event-duration">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="clock-icon"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{{ eventType.duration }} min</span>
                </div>

                <!-- Timezone selector for selected event -->
                <div
                  v-if="selectedEventType?.id === eventType.id"
                  class="timezone-section"
                >
                  <label class="timezone-label">Select Timezone:</label>
                  <select
                    v-model="selectedTimezone"
                    class="timezone-select"
                    @change="fetchAvailabilitySlots"
                  >
                    <option
                      v-for="tz in timezones"
                      :key="tz.value"
                      :value="tz.value"
                    >
                      {{ tz.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- CENTER PANEL: Calendar -->
            <div class="calendar-panel">
              <div class="calendar-header">
                <button
                  class="nav-btn"
                  :disabled="isPreviousMonthDisabled"
                  @click="previousMonth"
                >
                  <fluent-icon icon="chevron-left" size="20" />
                </button>
                <h3 class="month-title">{{ monthYearDisplay }}</h3>
                <button class="nav-btn" @click="nextMonth">
                  <fluent-icon icon="chevron-right" size="20" />
                </button>
              </div>

              <div class="calendar-grid">
                <div class="weekdays">
                  <span v-for="day in weekdays" :key="day" class="weekday">{{
                    day
                  }}</span>
                </div>
                <div class="days">
                  <div
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    :class="getDayClasses(day)"
                    @click="selectDate(day)"
                  >
                    {{ day.date || '' }}
                  </div>
                </div>
              </div>

              <div v-if="isLoadingSlots" class="loading-slots">
                <spinner size="small" />
                <span>{{ $t('SCHEDULE_DEMO.LOADING_SLOTS') }}</span>
              </div>
            </div>

            <!-- RIGHT PANEL: Time Slots -->
            <div class="slots-panel">
              <div v-if="selectedDate" class="slots-header">
                <h4 class="selected-date-title">{{ selectedDateDisplay }}</h4>
                <div class="time-format-toggle">
                  <button
                    :class="['toggle-btn', { active: timeFormat === '12h' }]"
                    @click="timeFormat = '12h'"
                  >
                    12h
                  </button>
                  <button
                    :class="['toggle-btn', { active: timeFormat === '24h' }]"
                    @click="timeFormat = '24h'"
                  >
                    24h
                  </button>
                </div>
              </div>

              <div v-if="!selectedDate" class="no-date-selected">
                <p>{{ $t('SCHEDULE_DEMO.SELECT_DATE') }}</p>
              </div>

              <div v-else-if="availableSlots.length === 0" class="no-slots">
                <p>{{ $t('SCHEDULE_DEMO.NO_SLOTS') }}</p>
              </div>

              <div v-else class="slots-list">
                <button
                  v-for="slot in availableSlots"
                  :key="slot"
                  :class="['slot-btn', { selected: selectedSlot === slot }]"
                  @click="selectSlot(slot)"
                >
                  {{ formatTime(slot) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Manual external meeting (same persistence as Kivo booking: contact additional_attributes) -->
        <div
          v-show="activeTab === 'manual'"
          class="tab-panel tab-panel--manual"
        >
          <div class="manual-meeting-form">
            <div class="form-group">
              <label class="form-label">
                {{ $t('SCHEDULE_DEMO.MANUAL_DATETIME_LABEL') }}
                <span class="required-star">*</span>
              </label>
              <woot-date-time-picker
                :value="manualScheduledAt"
                class="date-time-picker-modal"
                :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
                @change="onManualDatetimeChange"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                {{ $t('SCHEDULE_DEMO.MANUAL_SUBJECT_LABEL') }}
                <span class="required-star">*</span>
              </label>
              <input
                v-model="manualSubject"
                type="text"
                class="form-input"
                :placeholder="$t('SCHEDULE_DEMO.MANUAL_SUBJECT_PLACEHOLDER')"
              />
            </div>
            <div class="form-group">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.SELECT_LEAD_LABEL')
              }}</label>
              <div class="multiselect-wrap">
                <multiselect
                  v-model="selectedLeadUser"
                  :options="meetingRoleAgentOptions"
                  track-by="id"
                  label="name"
                  :placeholder="$t('SCHEDULE_DEMO.SELECT_LEAD_PLACEHOLDER')"
                  :allow-empty="true"
                  :close-on-select="true"
                  select-label=""
                  deselect-label=""
                />
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.SELECT_ACCOUNT_MANAGER_LABEL')
              }}</label>
              <div class="multiselect-wrap">
                <multiselect
                  v-model="selectedAccountManagerUser"
                  :options="meetingRoleAgentOptions"
                  track-by="id"
                  label="name"
                  :placeholder="
                    $t('SCHEDULE_DEMO.SELECT_ACCOUNT_MANAGER_PLACEHOLDER')
                  "
                  :allow-empty="true"
                  :close-on-select="true"
                  select-label=""
                  deselect-label=""
                />
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Add guest's email</label>
              <input
                v-model="guestEmail"
                type="email"
                :class="['form-input', { 'input-error': guestEmailError }]"
                placeholder="Add guest's email"
                @keyup.enter="addGuest"
                @input="clearGuestEmailError"
              />
              <span v-if="guestEmailError" class="error-message">{{
                guestEmailError
              }}</span>
              <button class="add-guest-btn" type="button" @click="addGuest">
                Add guests
              </button>
              <div v-if="guests.length > 0" class="guests-list">
                <div
                  v-for="(guest, idx) in guests"
                  :key="`manual-guest-${idx}`"
                  class="guest-tag"
                >
                  <span>{{ guest }}</span>
                  <button
                    class="remove-guest"
                    type="button"
                    @click="removeGuest(idx)"
                  >
                    <fluent-icon icon="dismiss" size="12" />
                  </button>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.MANUAL_DESCRIPTION_LABEL')
              }}</label>
              <textarea
                v-model="manualDescription"
                class="form-textarea"
                rows="3"
                :placeholder="
                  $t('SCHEDULE_DEMO.MANUAL_DESCRIPTION_PLACEHOLDER')
                "
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                {{ $t('SCHEDULE_DEMO.MANUAL_LINK_LABEL') }}
                <span class="required-star">*</span>
              </label>
              <input
                v-model="manualMeetingLink"
                type="url"
                class="form-input"
                :class="{ 'input-error': manualLinkError }"
                :placeholder="$t('SCHEDULE_DEMO.MANUAL_LINK_PLACEHOLDER')"
                @input="onManualMeetingLinkInput"
              />
              <span
                v-if="detectedManualMeetingPlatformLabel && !manualLinkError"
                class="detected-platform-hint"
              >
                {{
                  $t('SCHEDULE_DEMO.MANUAL_LINK_DETECTED', {
                    platform: detectedManualMeetingPlatformLabel,
                  })
                }}
              </span>
              <span v-if="manualLinkError" class="error-message">{{
                manualLinkError
              }}</span>
            </div>
            <div v-if="showPlatformSelector" class="form-group">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.PLATFORM_LABEL')
              }}</label>
              <div class="platform-selector platform-selector--external" role="radiogroup">
                <button
                  v-for="option in meetingPlatformOptions"
                  :key="`manual-${option.id}`"
                  type="button"
                  role="radio"
                  class="platform-option"
                  :class="{ selected: selectedPlatform === option.id }"
                  :aria-checked="selectedPlatform === option.id"
                  @click="selectManualPlatform(option.id)"
                >
                  <span class="platform-option__icon" aria-hidden="true">
                    <meeting-platform-icon
                      :platform-id="option.id"
                      :alt="option.label"
                    />
                  </span>
                  <span class="platform-option__label">{{ option.label }}</span>
                </button>
              </div>
            </div>
            <div class="form-group manual-labels-field">
              <label class="form-label">{{
                $t('SCHEDULE_DEMO.MANUAL_LABELS_LABEL')
              }}</label>
              <div class="multiselect-wrap--medium">
                <multiselect
                  v-model="manualSelectedLabels"
                  class="no-margin"
                  :options="allLabels"
                  :multiple="true"
                  :searchable="true"
                  track-by="id"
                  label="title"
                  :close-on-select="false"
                  :show-labels="false"
                  :placeholder="$t('SCHEDULE_DEMO.MANUAL_LABELS_PLACEHOLDER')"
                  :allow-empty="true"
                  :max-height="200"
                  open-direction="top"
                />
              </div>
            </div>
            <div class="manual-form-actions">
              <woot-button
                variant="smooth"
                color-scheme="secondary"
                @click="handleClose"
              >
                {{ $t('MERGE_CONTACTS.FORM.CANCEL') }}
              </woot-button>
              <woot-button
                v-if="isRescheduleMode"
                color-scheme="alert"
                variant="smooth"
                :is-loading="isCancelingManual"
                :is-disabled="isSavingManual"
                @click="confirmCancelManualMeeting"
              >
                {{ $t('SCHEDULE_DEMO.MANUAL_CANCEL') }}
              </woot-button>
              <woot-button
                color-scheme="primary"
                :is-loading="isSavingManual"
                :is-disabled="isCancelingManual"
                @click="saveManualMeeting"
              >
                {{
                  isRescheduleMode
                    ? $t('SCHEDULE_DEMO.MANUAL_RESCHEDULE')
                    : $t('SCHEDULE_DEMO.MANUAL_SAVE')
                }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
    <woot-confirm-modal
      ref="cancelMeetingDialog"
      :title="$t('EDIT_CONTACT.CANCEL_MEETING_CONFIRM_TITLE')"
      :description="$t('EDIT_CONTACT.CANCEL_MEETING_CONFIRM_DESCRIPTION')"
      :confirm-label="$t('EDIT_CONTACT.CANCEL_MEETING_CONFIRM')"
      :cancel-label="$t('EDIT_CONTACT.CANCEL_MEETING_KEEP')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
import ContactAPI from 'dashboard/api/contacts';
import userEventTypesAPI from 'dashboard/api/userEventTypes';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import MeetingPlatformIcon from 'dashboard/components/widgets/MeetingPlatformIcon.vue';
import {
  buildMeetingPlatformOptions,
  DEFAULT_KIVO_MEETING_TYPES,
  EXTERNAL_MEETING_TYPES,
  meetingTypeForPlatformId,
} from 'dashboard/constants/kivoMeetingPlatforms';
import {
  isValidGuestEmail,
  normalizeGuestEmail,
} from 'dashboard/helper/guestEmail';
import {
  detectMeetingPlatform,
  meetingLinkValidationError,
  parseMeetingUrl,
} from 'dashboard/helper/meetingLinkHelper';
import {
  canRescheduleMeeting,
  isMeetingOngoing,
  MEETING_LIFECYCLE,
  resolveMeetingLifecycle,
} from 'dashboard/helper/contactMeetingLifecycle';
import { persistContactMeetingUi } from 'dashboard/helper/contactMeetingUiSync';

export default {
  components: {
    Spinner,
    WootDateTimePicker,
    MeetingPlatformIcon,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contact: {
      type: Object,
      required: true,
    },
    showSwitchToReminder: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // Start on Kivo while loading; avoids External → Kivo tab flash when event types exist
      activeTab: 'kivo',
      manualScheduledAt: null,
      manualSubject: '',
      manualDescription: '',
      manualMeetingLink: '',
      manualLinkError: '',
      manualSelectedLabels: [],
      isSavingManual: false,
      isCancelingManual: false,
      eventTypes: [],
      selectedEventType: null,
      selectedTimezone: 'Asia/Kolkata',
      currentMonth: new Date().getMonth() + 1,
      currentYear: new Date().getFullYear(),
      selectedDate: null,
      availableSlots: [],
      selectedSlot: null,
      isInitializing: true,
      isLoadingSlots: false,
      isBooking: false,
      timeFormat: '12h',
      availabilityData: {},
      weekdays: ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'],
      // Booking form fields
      bookingSubject: '',
      selectedLeadUser: null,
      selectedAccountManagerUser: null,
      roleAgentExtras: [],
      savedMeetingRoles: {
        lead: null,
        accountManager: null,
        guests: [],
        subject: '',
      },
      selectedPlatform: 'google_meet',
      availableMeetingTypes: [],
      isLoadingEventDetails: false,
      guestEmail: '',
      guests: [],
      guestEmailError: '',
      timezones: [
        { value: 'Asia/Kolkata', label: 'Asia/Kolkata (GMT+05:30)' },
        { value: 'America/New_York', label: 'America/New_York (GMT-05:00)' },
        { value: 'Asia/Bangkok', label: 'Asia/Bangkok (GMT+07:00)' },
        { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (GMT+08:00)' },
        { value: 'Asia/Jakarta', label: 'Asia/Jakarta (GMT+07:00)' },
        { value: 'Asia/Seoul', label: 'Asia/Seoul (GMT+09:00)' },
        { value: 'Asia/Shanghai', label: 'Asia/Shanghai (GMT+08:00)' },
        { value: 'Asia/Singapore', label: 'Asia/Singapore (GMT+08:00)' },
        { value: 'Asia/Tokyo', label: 'Asia/Tokyo (GMT+09:00)' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      allLabels: 'labels/getLabels',
      availableAgents: 'agents/getAgents',
    }),

    meetingRoleAgentOptions() {
      const byId = new Map();
      [...(this.availableAgents || []), ...(this.roleAgentExtras || [])].forEach(
        agent => {
          if (agent?.id != null) {
            byId.set(String(agent.id), agent);
          }
        }
      );
      return Array.from(byId.values());
    },

    effectiveAvailableMeetingTypes() {
      if (this.activeTab === 'manual') {
        return EXTERNAL_MEETING_TYPES;
      }
      if (this.availableMeetingTypes?.length) {
        return this.availableMeetingTypes;
      }
      return [];
    },

    detectedManualMeetingPlatform() {
      return detectMeetingPlatform(this.manualMeetingLink);
    },

    detectedManualMeetingPlatformLabel() {
      if (!this.detectedManualMeetingPlatform) return '';
      const option = this.meetingPlatformOptions.find(
        item => item.id === this.detectedManualMeetingPlatform
      );
      return option?.label || '';
    },

    meetingPlatformOptions() {
      return buildMeetingPlatformOptions(
        this.effectiveAvailableMeetingTypes,
        key => this.$t(key)
      );
    },

    showPlatformSelector() {
      return this.meetingPlatformOptions.length > 0;
    },

    monthYearDisplay() {
      const date = new Date(this.currentYear, this.currentMonth - 1);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    },

    selectedDateDisplay() {
      if (!this.selectedDate) return '';
      const date = new Date(
        this.currentYear,
        this.currentMonth - 1,
        this.selectedDate
      );
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    },

    selectedDateDisplayFull() {
      if (!this.selectedDate) return '';
      const date = new Date(
        this.currentYear,
        this.currentMonth - 1,
        this.selectedDate
      );
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    },

    calendarDays() {
      const firstDay = new Date(
        this.currentYear,
        this.currentMonth - 1,
        1
      ).getDay();
      const daysInMonth = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate();
      const days = [];

      for (let i = 0; i < firstDay; i++) {
        days.push({ date: null, available: false });
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const dateKey = this.getDateKey(d);
        const slots = this.availabilityData[dateKey];
        const hasSlots = Array.isArray(slots) && slots.length > 0;
        days.push({
          date: d,
          available: hasSlots,
          isToday: this.isToday(d),
          isPast: this.isPast(d),
        });
      }

      return days;
    },

    isPreviousMonthDisabled() {
      const today = new Date();
      return (
        this.currentYear === today.getFullYear() &&
        this.currentMonth === today.getMonth() + 1
      );
    },

    meetingLifecycle() {
      return resolveMeetingLifecycle(this.contact);
    },

    isRescheduleMode() {
      if (this.contact?.schedule_intent === 'book') return false;
      return canRescheduleMeeting(this.contact);
    },

    showBookingTypeTabs() {
      return !this.isRescheduleMode;
    },

    isMeetingOngoing() {
      return isMeetingOngoing(this.contact);
    },

    scheduleModalTitle() {
      if (this.isRescheduleMode) {
        return this.$t('SCHEDULE_DEMO.TITLE_RESCHEDULE');
      }
      return this.$t('SCHEDULE_DEMO.TITLE_BOOK');
    },

    existingCalendarUniqueId() {
      const attrs = this.contact?.additional_attributes || {};
      return (
        attrs.calendar_unique_id ||
        attrs.unique_id ||
        attrs.uniqueId ||
        ''
      );
    },

    existingEventTypeId() {
      return this.contact?.additional_attributes?.event_id || '';
    },

    isExternalExistingMeeting() {
      if (!this.isRescheduleMode) return false;
      const provider = this.contact?.additional_attributes?.meeting_provider;
      if (provider === 'manual') return true;
      if (provider === 'kivo') return false;
      return !this.existingCalendarUniqueId;
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.resetManualMeetingForm();
        this.initialize();
        if (this.activeTab === 'manual') {
          this.ensureManualMeetingLabelsOptions();
        }
      } else {
        this.resetState();
      }
    },
    activeTab(val, oldVal) {
      if (val === 'manual' && this.show) {
        this.ensureManualMeetingLabelsOptions();
      }
      // Email is only required for Kivo booking — warn when user opens Kivo from External
      if (
        val === 'kivo' &&
        oldVal === 'manual' &&
        this.show &&
        !this.contact?.email &&
        this.eventTypes.length > 0
      ) {
        this.showAlert(this.$t('SCHEDULE_DEMO.CONTACT_EMAIL_REQUIRED'));
      }
    },
  },

  mounted() {
    if (this.show) {
      this.resetManualMeetingForm();
      this.initialize();
    }
  },

  methods: {
    async initialize() {
      this.isInitializing = true;
      this.eventTypes = [];
      this.activeTab = this.isExternalExistingMeeting ? 'manual' : 'kivo';

      try {
        await Promise.all([
          this.$store.dispatch('agents/get'),
          this.loadRescheduleMeetingDetails(),
        ]);
        this.prefillMeetingRoleDefaults();

        const eventTypesPromise = this.fetchEventTypes();
        const timeoutPromise = new Promise(resolve => {
          setTimeout(() => resolve([]), 8000);
        });

        this.eventTypes = await Promise.race([
          eventTypesPromise,
          timeoutPromise,
        ]);

        if (this.eventTypes.length > 0) {
          const existingEventType = this.eventTypes.find(
            eventType =>
              String(eventType.event_id) === String(this.existingEventTypeId)
          );
          await this.selectEventType(existingEventType || this.eventTypes[0]);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        this.eventTypes = [];
      } finally {
        this.isInitializing = false;
        if (this.isExternalExistingMeeting || this.eventTypes.length === 0) {
          this.activeTab = 'manual';
        }
      }
    },

    async fetchEventTypes() {
      try {
        const response = await userEventTypesAPI.getAll();
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching event types:', error);
        return [];
      }
    },

    async selectEventType(eventType) {
      this.selectedEventType = eventType;
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      this.availabilityData = {};
      // Reset to current month when changing event type
      this.currentMonth = new Date().getMonth() + 1;
      this.currentYear = new Date().getFullYear();
      await this.fetchEventMeetingPlatforms(eventType);
      await this.fetchAvailabilitySlots();
      // Auto-select today if available, otherwise first available day
      this.autoSelectFirstAvailableDate();
    },

    async fetchAvailabilitySlots() {
      if (!this.selectedEventType) return;

      if (!this.contact.email) {
        this.availabilityData = {};
        if (this.activeTab === 'kivo') {
          this.showAlert(this.$t('SCHEDULE_DEMO.CONTACT_EMAIL_REQUIRED'));
        }
        return;
      }

      this.isLoadingSlots = true;
      try {
        const response = await userEventTypesAPI.getAvailabilitySlots({
          eventId: this.selectedEventType.event_id,
          scheduleId: this.selectedEventType.schedule_id,
          month: this.currentMonth,
          year: this.currentYear,
          timeZone: this.selectedTimezone,
          contactEmail: this.contact.email,
        });

        if (response.data?.success) {
          const data = response.data.data;
          this.availabilityData = data && typeof data === 'object' ? data : {};
        } else {
          this.availabilityData = {};
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        this.availabilityData = {};
      } finally {
        this.isLoadingSlots = false;
      }
    },

    selectDate(day) {
      if (!day.date || day.isPast || !day.available) return;

      this.selectedDate = day.date;
      this.selectedSlot = null;

      const dateKey = this.getDateKey(day.date);
      this.availableSlots = this.availabilityData[dateKey] || [];
    },

    selectSlot(slot) {
      this.selectedSlot = slot;
      this.bookingSubject =
        this.isRescheduleMode && this.savedMeetingRoles.subject
          ? this.savedMeetingRoles.subject
          : `Demo with ${this.contact.name || 'Guest'}`;
      this.prefillMeetingRoleDefaults();
    },

    clearSlotSelection() {
      this.selectedSlot = null;
      this.bookingSubject = '';
      this.resetMeetingRoleFields();
      this.guestEmail = '';
      this.guests = [];
      this.guestEmailError = '';
    },

    findAgentById(id) {
      if (id == null || id === '') return null;
      return (
        this.meetingRoleAgentOptions.find(
          agent => String(agent.id) === String(id)
        ) || null
      );
    },

    savedGuestEmails() {
      const fromMeeting = this.savedMeetingRoles.guests || [];
      if (fromMeeting.length) return [...fromMeeting];
      const raw = this.contact?.additional_attributes?.guest_emails;
      if (!Array.isArray(raw)) return [];
      return raw.map(email => String(email || '').trim()).filter(Boolean);
    },

    normalizedGuestEmails() {
      return (this.guests || [])
        .map(email => normalizeGuestEmail(email))
        .filter(Boolean)
        .filter((email, index, list) => list.indexOf(email) === index);
    },

    async loadRescheduleMeetingDetails() {
      if (!this.isRescheduleMode || !this.contact?.id) return;

      try {
        const { data } = await ContactAPI.getContact(this.contact.id);
        const payload = data?.payload || data;
        if (payload && typeof payload === 'object') {
          this.mergeContactMeetingFields(payload);
          const assigneeId = payload.assignee_id || payload.assignee?.id;
          if (assigneeId != null) {
            this.$set(this.contact, 'assignee_id', assigneeId);
          }
          if (payload.primary_contact_meeting_id) {
            this.$set(
              this.contact,
              'primary_contact_meeting_id',
              payload.primary_contact_meeting_id
            );
          }
        }
      } catch (error) {
        console.error('Error loading contact for reschedule:', error);
      }

      const meetingId = this.contact.primary_contact_meeting_id;
      if (!meetingId) return;

      try {
        const { data } = await ContactAPI.getMeeting(this.contact.id, meetingId);
        this.applyMeetingRolesFromRecord(data);
      } catch (error) {
        console.error('Error loading meeting for reschedule:', error);
      }
    },

    participantAsAgent(participant) {
      if (!participant?.user_id) return null;
      return {
        id: participant.user_id,
        name: participant.display_name || participant.email || '',
        email: participant.email || '',
      };
    },

    applyMeetingRolesFromRecord(meeting) {
      if (!meeting || typeof meeting !== 'object') return;

      const participants = Array.isArray(meeting.participants)
        ? meeting.participants
        : [];
      const leadFromParticipant = this.participantAsAgent(
        participants.find(item => item.participant_type === 'demo_lead')
      );
      const managerFromParticipant = this.participantAsAgent(
        participants.find(item => item.participant_type === 'account_manager')
      );

      const extras = [];
      const lead = meeting.lead?.id
        ? meeting.lead
        : meeting.lead_user_id
          ? leadFromParticipant
          : null;
      const accountManager = meeting.account_manager?.id
        ? meeting.account_manager
        : meeting.account_manager_user_id
          ? managerFromParticipant
          : null;
      if (lead?.id) {
        extras.push(lead);
        this.savedMeetingRoles.lead = lead;
      }
      if (accountManager?.id) {
        extras.push(accountManager);
        this.savedMeetingRoles.accountManager = accountManager;
      }
      this.roleAgentExtras = extras;

      const guestEmails = Array.isArray(meeting.metadata?.guest_emails)
        ? meeting.metadata.guest_emails
        : [];
      const participantGuests = Array.isArray(meeting.participants)
        ? meeting.participants
            .filter(participant => participant.participant_type === 'guest')
            .map(participant => participant.email)
        : [];
      this.savedMeetingRoles.guests = [
        ...guestEmails,
        ...participantGuests,
      ]
        .map(email => String(email || '').trim())
        .filter(Boolean)
        .filter((email, index, list) => list.indexOf(email) === index);

      this.savedMeetingRoles.subject = meeting.title || '';

      if (!this.contact.additional_attributes) {
        this.$set(this.contact, 'additional_attributes', {});
      }
      const leadId = meeting.lead?.id || meeting.lead_user_id || null;
      const managerId =
        meeting.account_manager?.id || meeting.account_manager_user_id || null;
      if (leadId != null) {
        this.$set(this.contact.additional_attributes, 'demo_lead_user_id', leadId);
      } else {
        this.$delete(this.contact.additional_attributes, 'demo_lead_user_id');
      }
      if (managerId != null) {
        this.$set(
          this.contact.additional_attributes,
          'account_manager_user_id',
          managerId
        );
      } else {
        this.$delete(
          this.contact.additional_attributes,
          'account_manager_user_id'
        );
      }
      if (this.savedMeetingRoles.guests.length) {
        this.$set(
          this.contact.additional_attributes,
          'guest_emails',
          this.savedMeetingRoles.guests
        );
      }
      if (meeting.meeting_url) {
        this.$set(this.contact.additional_attributes, 'meeting_link', meeting.meeting_url);
        this.manualMeetingLink = meeting.meeting_url;
      }
      if (meeting.meeting_platform) {
        this.$set(
          this.contact.additional_attributes,
          'meeting_platform',
          meeting.meeting_platform
        );
        this.selectedPlatform = meeting.meeting_platform;
      }
      if (meeting.external_provider) {
        this.$set(
          this.contact.additional_attributes,
          'meeting_provider',
          meeting.external_provider
        );
      }
      if (meeting.id) {
        this.$set(this.contact, 'primary_contact_meeting_id', meeting.id);
      }
      this.applyDetectedMeetingPlatform();
    },

    prefillMeetingRoleDefaults() {
      const attrs = this.contact?.additional_attributes || {};

      if (this.isRescheduleMode) {
        this.selectedLeadUser =
          this.savedMeetingRoles.lead ||
          this.findAgentById(attrs.demo_lead_user_id);
        this.selectedAccountManagerUser =
          this.savedMeetingRoles.accountManager ||
          this.findAgentById(attrs.account_manager_user_id);
        this.guests = this.savedGuestEmails();
        return;
      }

      this.selectedAccountManagerUser = null;
      this.selectedLeadUser = this.findAgentById(this.contact?.assignee_id);
    },

    resetMeetingRoleFields() {
      this.selectedLeadUser = null;
      this.selectedAccountManagerUser = null;
      this.selectedPlatform = 'google_meet';
      this.availableMeetingTypes = [];
    },

    async fetchEventMeetingPlatforms(eventType) {
      if (!eventType?.event_id) {
        this.availableMeetingTypes = [];
        return;
      }

      this.isLoadingEventDetails = true;
      try {
        const response = await userEventTypesAPI.fetchEventDetails(
          eventType.event_id
        );
        const types = response.data?.data?.availableMeetingTypes;
        this.availableMeetingTypes = Array.isArray(types) ? types : [];
      } catch (error) {
        console.error('Error fetching event meeting platforms:', error);
        this.availableMeetingTypes = [...DEFAULT_KIVO_MEETING_TYPES];
      } finally {
        this.isLoadingEventDetails = false;
        this.applyDefaultPlatformSelection();
      }
    },

    applyDefaultPlatformSelection() {
      const options = this.meetingPlatformOptions;
      if (!options.length) return;

      const isCurrentValid = options.some(
        option => option.id === this.selectedPlatform
      );
      if (!isCurrentValid) {
        this.selectedPlatform = options[0].id;
      }
    },

    onManualMeetingLinkInput() {
      this.applyDetectedMeetingPlatform();
    },

    applyDetectedMeetingPlatform() {
      const detected = detectMeetingPlatform(this.manualMeetingLink);
      if (detected) {
        this.selectedPlatform = detected;
        this.manualLinkError = '';
        return;
      }

      const parsed = parseMeetingUrl(this.manualMeetingLink);
      if (!parsed || !parsed.hostname?.includes('.')) {
        this.manualLinkError = '';
        return;
      }
      this.manualLinkError = this.manualLinkErrorMessage('unrecognized');
    },

    selectManualPlatform(platformId) {
      this.selectedPlatform = platformId;
      if (!this.manualMeetingLink?.trim()) {
        this.manualLinkError = '';
        return;
      }
      const errorCode = meetingLinkValidationError(
        this.manualMeetingLink,
        platformId
      );
      this.manualLinkError = errorCode
        ? this.manualLinkErrorMessage(errorCode)
        : '';
    },

    manualLinkErrorMessage(errorCode) {
      if (errorCode === 'required') {
        return this.$t('SCHEDULE_DEMO.MANUAL_LINK_REQUIRED');
      }
      if (errorCode === 'mismatch') {
        return this.$t('SCHEDULE_DEMO.MANUAL_LINK_PLATFORM_MISMATCH');
      }
      return this.$t('SCHEDULE_DEMO.MANUAL_INVALID_LINK');
    },

    buildKivoGuestList() {
      const emails = new Set(this.normalizedGuestEmails());
      [this.selectedLeadUser, this.selectedAccountManagerUser].forEach(user => {
        const email = user?.email?.trim();
        if (email) {
          emails.add(email);
        }
      });
      return Array.from(emails);
    },

    kivoMeetingTypeForPlatform() {
      return meetingTypeForPlatformId(this.selectedPlatform);
    },

    meetingParticipantsPayload() {
      return {
        meeting_platform: this.selectedPlatform,
        account_manager_user_id: this.selectedAccountManagerUser?.id || null,
        demo_lead_user_id: this.selectedLeadUser?.id || null,
        guest_emails: this.normalizedGuestEmails(),
      };
    },

    addGuest() {
      const email = normalizeGuestEmail(this.guestEmail);
      this.guestEmailError = '';

      if (!email) {
        this.guestEmailError = this.$t('SCHEDULE_DEMO.GUEST_EMAIL_REQUIRED');
        return false;
      }

      if (!isValidGuestEmail(email)) {
        this.guestEmailError = this.$t('SCHEDULE_DEMO.GUEST_EMAIL_INVALID');
        return false;
      }

      if (this.normalizedGuestEmails().includes(email)) {
        this.guestEmailError = this.$t('SCHEDULE_DEMO.GUEST_EMAIL_DUPLICATE');
        return false;
      }

      this.guests.push(email);
      this.guestEmail = '';
      return true;
    },

    ensureGuestEmailsValid() {
      if (normalizeGuestEmail(this.guestEmail) && !this.addGuest()) {
        this.showAlert(this.guestEmailError);
        return false;
      }

      const invalid = this.normalizedGuestEmails().find(
        email => !isValidGuestEmail(email)
      );
      if (invalid) {
        this.guestEmailError = this.$t('SCHEDULE_DEMO.GUEST_EMAIL_INVALID');
        this.showAlert(this.guestEmailError);
        return false;
      }

      return true;
    },

    removeGuest(index) {
      this.guests.splice(index, 1);
    },

    clearGuestEmailError() {
      this.guestEmailError = '';
    },

    resetManualMeetingForm() {
      const attrs = this.contact?.additional_attributes || {};
      const existingStart = this.contact?.meeting_starts_at;
      this.manualScheduledAt =
        this.isRescheduleMode && existingStart
          ? new Date(existingStart)
          : new Date();
      this.manualSubject = this.isRescheduleMode
        ? attrs.demo_subject || ''
        : '';
      this.manualDescription = this.isRescheduleMode
        ? attrs.meeting_description || ''
        : '';
      this.manualMeetingLink = this.isRescheduleMode
        ? attrs.meeting_link || ''
        : '';
      this.manualLinkError = '';
      this.manualSelectedLabels = [];
      this.selectedAccountManagerUser = null;
      if (this.isRescheduleMode && attrs.meeting_platform) {
        this.selectedPlatform = attrs.meeting_platform;
      }
      this.applyDetectedMeetingPlatform();
      this.prefillMeetingRoleDefaults();
    },

    async ensureManualMeetingLabelsOptions() {
      try {
        if (!this.allLabels?.length) {
          await this.$store.dispatch('labels/get');
        }
        await this.$store.dispatch('contactLabels/get', this.contact.id);
      } catch (e) {
        // Labels are optional; ignore load failures
      }
    },

    onManualDatetimeChange(value) {
      this.manualScheduledAt = value;
    },

    buildCalendarTimingsFromDate(date) {
      if (!date || Number.isNaN(new Date(date).getTime())) return '';
      const d = new Date(date);
      const dateString = d.toString();
      const dateParts = dateString.split(' ');
      if (dateParts.length < 6 || !dateParts[4] || !dateParts[5]) return '';
      const timeParts = dateParts[4].split(':');
      if (timeParts.length < 3) return '';
      let hour = parseInt(timeParts[0], 10);
      const minute = parseInt(timeParts[1], 10);
      const second = parseInt(timeParts[2], 10);
      const dayName = dateParts[0];
      const monthName = dateParts[1];
      const day = dateParts[2];
      const year = dateParts[3];
      const tz = dateParts[5];
      if (tz.length < 8) return '';
      const gmtOffset = tz.substring(3, 8);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour %= 12;
      hour = hour || 12;
      return `${dayName}, ${monthName} ${day}, ${year}, ${hour}:${String(
        minute
      ).padStart(2, '0')}:${second} ${ampm} ${gmtOffset}`;
    },

    async applyBookedMeetingToContactUi({
      contactPayload,
      calendarTimings,
      meetLink,
      demoSubject,
    }) {
      const attrs = contactPayload?.additional_attributes || {};
      const meetingPayload = {
        contactId: this.contact.id,
        demo_timing: attrs.demo_timing || calendarTimings,
        meeting_link: attrs.meeting_link || meetLink,
        demo_subject: attrs.demo_subject || demoSubject,
        meeting_description: attrs.meeting_description ?? null,
        meeting_starts_at: contactPayload?.meeting_starts_at,
        meeting_ends_at: contactPayload?.meeting_ends_at,
        meeting_lifecycle: contactPayload?.meeting_lifecycle || 'future',
        primary_contact_meeting_id: contactPayload?.primary_contact_meeting_id,
        meeting_platform: attrs.meeting_platform,
        meeting_provider: attrs.meeting_provider,
      };

      await persistContactMeetingUi(this.$store, meetingPayload);

      if (typeof bus !== 'undefined' && bus && bus.$emit) {
        bus.$emit('demo-meeting-updated', meetingPayload);
      }
    },

    mergeContactMeetingFields(contactPayload) {
      if (!contactPayload) return;

      if (contactPayload.meeting_starts_at) {
        this.$set(this.contact, 'meeting_starts_at', contactPayload.meeting_starts_at);
      }
      if (contactPayload.meeting_ends_at) {
        this.$set(this.contact, 'meeting_ends_at', contactPayload.meeting_ends_at);
      }
      if (contactPayload.meeting_lifecycle) {
        this.$set(this.contact, 'meeting_lifecycle', contactPayload.meeting_lifecycle);
        this.$set(
          this.contact,
          'can_book_meeting',
          contactPayload.meeting_lifecycle === 'none'
        );
        this.$set(
          this.contact,
          'can_reschedule_meeting',
          contactPayload.meeting_lifecycle === 'future' ||
            contactPayload.meeting_lifecycle === 'ongoing'
        );
        this.$set(
          this.contact,
          'can_cancel_meeting',
          contactPayload.meeting_lifecycle === 'future' ||
            contactPayload.meeting_lifecycle === 'ongoing'
        );
      }

      const attrs = contactPayload.additional_attributes;
      if (!attrs || typeof attrs !== 'object') return;

      if (!this.contact.additional_attributes) {
        this.$set(this.contact, 'additional_attributes', {});
      }
      [
        'demo_timing',
        'meeting_link',
        'demo_subject',
        'event_id',
        'meeting_platform',
        'meeting_provider',
      ].forEach(key => {
        if (attrs[key]) {
          this.$set(this.contact.additional_attributes, key, attrs[key]);
        }
      });
      ['demo_lead_user_id', 'account_manager_user_id', 'guest_emails'].forEach(
        key => {
          if (Object.prototype.hasOwnProperty.call(attrs, key)) {
            this.$set(this.contact.additional_attributes, key, attrs[key]);
          }
        }
      );
      if (attrs.meeting_description === undefined) {
        this.$delete(this.contact.additional_attributes, 'meeting_description');
      }
    },

    manualMeetingLinkValid(link) {
      return !meetingLinkValidationError(link, this.selectedPlatform);
    },

    async saveManualMeeting() {
      const wasReschedule = this.isRescheduleMode;
      const subject = (this.manualSubject || '').trim();
      const meetLink = (this.manualMeetingLink || '').trim();
      if (!this.manualScheduledAt || !subject) {
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_VALIDATION'));
        return;
      }
      if (!meetLink) {
        this.manualLinkError = this.$t('SCHEDULE_DEMO.MANUAL_LINK_REQUIRED');
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_LINK_REQUIRED'));
        return;
      }
      const linkError = meetingLinkValidationError(meetLink, this.selectedPlatform);
      if (linkError) {
        this.manualLinkError = this.manualLinkErrorMessage(linkError);
        this.showAlert(this.manualLinkError);
        return;
      }
      this.manualLinkError = '';

      const calendarTimings = this.buildCalendarTimingsFromDate(
        this.manualScheduledAt
      );
      if (!calendarTimings) {
        this.showAlert(this.$t('SCHEDULE_DEMO.MANUAL_VALIDATION'));
        return;
      }
      if (!this.ensureGuestEmailsValid()) return;
      const description = (this.manualDescription || '').trim();

      this.isSavingManual = true;
      try {
        const response = await this.$store.dispatch(
          'contacts/save_demo_meeting',
          {
            contactId: this.contact.id,
            data: {
              demo_timing: calendarTimings,
              meeting_link: meetLink,
              demo_subject: subject,
              meeting_description: description,
              manual_external_booking: true,
              meeting_id: wasReschedule
                ? this.contact.primary_contact_meeting_id
                : null,
              ...this.meetingParticipantsPayload(),
            },
          }
        );

        if (response?.success) {
          const startDate = new Date(this.manualScheduledAt);
          const startsAtIso = startDate.toISOString();
          const savedMeetingId = response.meeting?.id || response.meeting_id;
          const meetingEndsAt =
            response.meeting?.ends_at || response.meeting_ends_at || undefined;
          const meetingPayload = {
            contactId: this.contact.id,
            demo_timing: calendarTimings,
            meeting_link: meetLink,
            demo_subject: subject,
            meeting_description: description || null,
            meeting_starts_at: startsAtIso,
            meeting_ends_at: meetingEndsAt,
            meeting_lifecycle: 'future',
            primary_contact_meeting_id: savedMeetingId || null,
            meeting_platform: this.selectedPlatform,
            meeting_provider: 'manual',
          };

          await persistContactMeetingUi(this.$store, meetingPayload);

          if (typeof bus !== 'undefined' && bus && bus.$emit) {
            bus.$emit('demo-meeting-updated', meetingPayload);
          }

          const labelTitles = (this.manualSelectedLabels || [])
            .map(l => l && l.title)
            .filter(Boolean);
          if (labelTitles.length) {
            try {
              const existing =
                this.$store.getters['contactLabels/getContactLabels'](
                  this.contact.id
                ) || [];
              const merged = [...new Set([...existing, ...labelTitles])];
              await this.$store.dispatch('contactLabels/update', {
                contactId: this.contact.id,
                labels: merged,
              });
            } catch (labelErr) {
              console.error('Error assigning labels:', labelErr);
              this.showAlert(this.$t('CONTACT_PANEL.LABELS.CONTACT.ERROR'));
            }
          }

          const optionsDate = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          };
          const optionsTime = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          };
          this.$emit('demo-scheduled', {
            meetLink,
            scheduledAt: startDate,
            formattedDate: startDate.toLocaleDateString('en-US', optionsDate),
            formattedTime: startDate.toLocaleTimeString('en-US', optionsTime),
            eventType: null,
            demoTiming: calendarTimings,
            manual: true,
          });

          this.showAlert(
            wasReschedule
              ? this.$t('SCHEDULE_DEMO.RESCHEDULE_SUCCESS')
              : this.$t('SCHEDULE_DEMO.SUCCESS')
          );
          this.onClose();
        } else {
          this.showAlert(
            response?.error || this.$t('SCHEDULE_DEMO.MANUAL_ERROR')
          );
        }
      } catch (error) {
        console.error('Error saving manual meeting:', error);
        this.showAlert(
          error.response?.data?.error || this.$t('SCHEDULE_DEMO.MANUAL_ERROR')
        );
      } finally {
        this.isSavingManual = false;
      }
    },

    async confirmCancelManualMeeting() {
      if (!this.isRescheduleMode) {
        this.showAlert(this.$t('SCHEDULE_DEMO.IN_PROGRESS_MESSAGE'));
        return;
      }

      const meetingId = this.contact?.primary_contact_meeting_id;
      if (!meetingId) {
        this.showAlert(this.$t('EDIT_CONTACT.CANCEL_MEETING_MISSING'));
        return;
      }

      const confirmed = await this.$refs.cancelMeetingDialog?.showConfirmation();
      if (!confirmed) return;

      this.isCancelingManual = true;
      try {
        await this.$store.dispatch('contacts/cancel_demo_meeting', {
          contactId: this.contact.id,
          meetingId,
        });

        this.$set(this.contact, 'meeting_starts_at', null);
        this.$set(this.contact, 'meeting_ends_at', null);
        this.$set(this.contact, 'meeting_lifecycle', 'none');
        this.$set(this.contact, 'primary_contact_meeting_id', null);
        this.$set(this.contact, 'can_book_meeting', true);
        this.$set(this.contact, 'can_reschedule_meeting', false);
        this.$set(this.contact, 'can_cancel_meeting', false);
        if (this.contact.additional_attributes) {
          [
            'demo_timing',
            'meeting_link',
            'demo_subject',
            'meeting_description',
            'meeting_platform',
            'meeting_provider',
            'event_id',
            'calendar_unique_id',
            'unique_id',
          ].forEach(key => {
            this.$delete(this.contact.additional_attributes, key);
          });
        }

        if (typeof bus !== 'undefined' && bus && bus.$emit) {
          bus.$emit('demo-meeting-updated', {
            contactId: this.contact.id,
            demo_timing: null,
            meeting_link: null,
            demo_subject: null,
            meeting_description: null,
            meeting_starts_at: null,
            meeting_ends_at: null,
          });
        }

        this.showAlert(this.$t('EDIT_CONTACT.CANCEL_MEETING_SUCCESS'));
        this.onClose();
      } catch (error) {
        this.showAlert(
          error.response?.data?.error ||
            error.message ||
            this.$t('EDIT_CONTACT.CANCEL_MEETING_ERROR')
        );
      } finally {
        this.isCancelingManual = false;
      }
    },

    formatEndTime(slot) {
      if (!slot || !this.selectedEventType) return '';
      const [hours, minutes] = slot.split(':');
      const startDate = new Date();
      startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      const endDate = new Date(
        startDate.getTime() +
          (this.selectedEventType.duration || 15) * 60 * 1000
      );
      const h = endDate.getHours();
      const m = endDate
        .getMinutes()
        .toString()
        .padStart(2, '0');

      if (this.timeFormat === '24h') {
        return `${h.toString().padStart(2, '0')}:${m}`;
      }
      const period = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${m} ${period}`;
    },

    async bookDemo() {
      const wasReschedule = this.isRescheduleMode;
      if (!this.selectedSlot || !this.selectedEventType) return;
      if (!this.ensureGuestEmailsValid()) return;

      if (!this.contact.email) {
        if (this.activeTab === 'kivo') {
          this.showAlert(this.$t('SCHEDULE_DEMO.CONTACT_EMAIL_REQUIRED'));
        }
        return;
      }

      this.isBooking = true;
      try {
        const [hours, minutes] = this.selectedSlot.split(':');
        const startDate = new Date(
          this.currentYear,
          this.currentMonth - 1,
          this.selectedDate,
          parseInt(hours, 10),
          parseInt(minutes, 10)
        );

        const durationInMs =
          (this.selectedEventType.duration || 15) * 60 * 1000;
        const endDate = new Date(startDate.getTime() + durationInMs);

        const calendarTimings = this.buildCalendarTimingsFromDate(startDate);

        const appointmentData = {
          scheduleType: 'demo',
          calendar_timings: calendarTimings,
          meetingLink: '',
          guests: this.buildKivoGuestList(),
          userEmail: this.contact.email,
          username: this.contact.name || 'Guest',
          meetingType: this.kivoMeetingTypeForPlatform(),
          platform: this.selectedPlatform,
          subject:
            this.bookingSubject || `Demo with ${this.contact.name || 'Guest'}`,
          endTime: endDate.getTime(),
          timeZone: this.selectedTimezone,
          startTime: startDate.getTime(),
          eventTypeId: this.selectedEventType.event_id,
          title: this.selectedEventType.name || 'Demo',
          status: 'confirmed',
          contact_id: this.contact.id,
          ...this.meetingParticipantsPayload(),
        };

        const shouldReschedule =
          this.isRescheduleMode && !!this.existingCalendarUniqueId;
        if (shouldReschedule) {
          appointmentData.uniqueId = this.existingCalendarUniqueId;
        }

        const response = shouldReschedule
          ? await userEventTypesAPI.rescheduleAppointment(appointmentData)
          : await userEventTypesAPI.bookAppointment(appointmentData);

        if (response.data?.success) {
          const bookingData = response.data?.data || {};
          const meetLink =
            bookingData?.calendarEventData?.hangoutLink ||
            bookingData?.meetingLink ||
            bookingData?.meetLink ||
            '';
          const calendarUniqueId =
            bookingData?.uniqueId ||
            bookingData?.unique_id ||
            bookingData?.bookingData?.uniqueId ||
            null;

          const optionsDate = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          };
          const optionsTime = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          };
          const formattedDate = startDate.toLocaleDateString(
            'en-US',
            optionsDate
          );
          const formattedTime = startDate.toLocaleTimeString(
            'en-US',
            optionsTime
          );

          const demoSubject =
            this.bookingSubject || `Demo with ${this.contact.name || 'Guest'}`;
          this.applyBookedMeetingToContactUi({
            contactPayload: response.data?.contact,
            calendarTimings,
            meetLink,
            demoSubject,
          });

          this.$emit('demo-scheduled', {
            meetLink,
            scheduledAt: startDate,
            formattedDate,
            formattedTime,
            eventType: this.selectedEventType.name,
            demoTiming: calendarTimings,
          });

          this.showAlert(
            wasReschedule
              ? this.$t('SCHEDULE_DEMO.RESCHEDULE_SUCCESS')
              : this.$t('SCHEDULE_DEMO.SUCCESS')
          );
          this.onClose();
        } else {
          this.showAlert(
            response.data?.error || this.$t('SCHEDULE_DEMO.ERROR')
          );
        }
      } catch (error) {
        console.error('Error booking demo:', error);
        this.showAlert(
          error.response?.data?.error || this.$t('SCHEDULE_DEMO.ERROR')
        );
      } finally {
        this.isBooking = false;
      }
    },

    async previousMonth() {
      if (this.currentMonth === 1) {
        this.currentMonth = 12;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      await this.fetchAvailabilitySlots();
      this.autoSelectFirstAvailableDate();
    },

    async nextMonth() {
      if (this.currentMonth === 12) {
        this.currentMonth = 1;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      await this.fetchAvailabilitySlots();
      this.autoSelectFirstAvailableDate();
    },

    autoSelectFirstAvailableDate() {
      const today = new Date();
      const isCurrentMonth =
        this.currentYear === today.getFullYear() &&
        this.currentMonth === today.getMonth() + 1;

      // Try to select today first (if current month and today has slots)
      if (isCurrentMonth) {
        const todayKey = this.getDateKey(today.getDate());
        const todaySlots = this.availabilityData[todayKey];
        if (Array.isArray(todaySlots) && todaySlots.length > 0) {
          this.selectedDate = today.getDate();
          this.availableSlots = todaySlots;
          return;
        }
      }

      // Otherwise find the first available day in the month
      const daysInMonth = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate();
      const startDay = isCurrentMonth ? today.getDate() : 1;

      for (let d = startDay; d <= daysInMonth; d++) {
        const dateKey = this.getDateKey(d);
        const slots = this.availabilityData[dateKey];
        if (Array.isArray(slots) && slots.length > 0) {
          this.selectedDate = d;
          this.availableSlots = slots;
          return;
        }
      }
    },

    formatTime(slot) {
      if (!slot) return '';
      const [hours, minutes] = slot.split(':');
      const h = parseInt(hours, 10);
      const m = minutes || '00';

      if (this.timeFormat === '24h') {
        return `${hours}:${m}`;
      }

      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${m} ${period}`;
    },

    getDateKey(day) {
      return `${this.currentYear}-${String(this.currentMonth).padStart(
        2,
        '0'
      )}-${String(day).padStart(2, '0')}`;
    },

    isToday(day) {
      const today = new Date();
      return (
        day === today.getDate() &&
        this.currentMonth === today.getMonth() + 1 &&
        this.currentYear === today.getFullYear()
      );
    },

    isPast(day) {
      const date = new Date(this.currentYear, this.currentMonth - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },

    getDayClasses(day) {
      return {
        'calendar-day': true,
        empty: !day.date,
        today: day.isToday,
        past: day.isPast,
        available: day.available && !day.isPast,
        selected: day.date === this.selectedDate,
      };
    },

    goToProfileSettings() {
      this.$router.push({
        name: 'profile_settings_index',
        query: { focus: 'event-types' },
      });
      this.onClose();
    },

    switchToReminder() {
      this.$emit('switch-to-reminder');
    },

    handleClose() {
      this.onClose();
    },

    resetState() {
      this.resetManualMeetingForm();
      this.selectedEventType = null;
      this.selectedDate = null;
      this.selectedSlot = null;
      this.availableSlots = [];
      this.availabilityData = {};
      this.currentMonth = new Date().getMonth() + 1;
      this.currentYear = new Date().getFullYear();
      this.bookingSubject = '';
      this.resetMeetingRoleFields();
      this.roleAgentExtras = [];
      this.savedMeetingRoles = {
        lead: null,
        accountManager: null,
        guests: [],
        subject: '',
      };
      this.guestEmail = '';
      this.guests = [];
      this.guestEmailError = '';
    },

    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
// Global styles to ensure modal appears above everything else
.schedule-demo-modal-wrapper.modal-mask,
.modal-mask.schedule-demo-modal-wrapper {
  z-index: 99999 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

.schedule-demo-modal-wrapper .modal-container {
  width: 95% !important;
  max-width: 950px !important;
  max-height: 520px !important;
  height: 520px !important;
  overflow: hidden !important;
  z-index: 100000 !important;
  border-radius: 16px !important;
}
</style>

<style lang="scss" scoped>
.schedule-demo-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.modal-tab-panels {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tab-panel--kivo {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tab-panel--manual {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-medium);
}

.required-star {
  color: var(--r-500);
}

.manual-meeting-form {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-small);

  .form-group {
    margin-bottom: 0;
  }

  .form-label {
    display: block;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--s-800);
    margin-bottom: var(--space-smaller);
  }

  .form-input,
  .form-textarea {
    width: 100%;
    padding: var(--space-small);
    font-size: var(--font-size-default);
    border: 1px solid var(--s-200);
    border-radius: var(--border-radius-normal);
    background: white;
    color: var(--s-900);

    &:focus {
      outline: none;
      border-color: var(--w-500);
      box-shadow: 0 0 0 1px var(--w-200);
    }

    &::placeholder {
      color: var(--s-400);
    }

    &.input-error {
      border-color: var(--r-500);
    }
  }

  .form-textarea {
    resize: vertical;
    min-height: 72px;
  }

  .error-message {
    display: block;
    margin-top: var(--space-micro);
    font-size: var(--font-size-mini);
    color: var(--r-500);
  }

  .detected-platform-hint {
    display: block;
    margin-top: var(--space-micro);
    font-size: var(--font-size-mini);
    color: var(--g-600);
  }

  /* vue-multiselect: multiselect-wrap--medium uses fixed h-12 + nowrap flex — unwrap for many labels */
  .manual-labels-field {
    ::v-deep .multiselect-wrap--medium .multiselect {
      height: auto;
      min-height: 3rem;
    }

    ::v-deep .multiselect-wrap--medium .multiselect__tags {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      align-content: flex-start;
      gap: 4px;
      min-height: 3rem;
      height: auto;
      overflow: visible;
      padding-top: 6px;
      padding-bottom: 6px;
    }

    ::v-deep .multiselect-wrap--medium .multiselect__tags-wrap {
      display: flex;
      flex-wrap: wrap;
      flex: 1 1 100%;
      min-width: 0;
      max-width: 100%;
      gap: 4px;
      align-items: center;
    }

    ::v-deep .multiselect-wrap--medium .multiselect__tag {
      flex-shrink: 0;
      margin-top: 0;
      margin-bottom: 0;
    }

    /*
     * Do not override .multiselect__input width — vue-multiselect uses inline width:0
     * when closed (multiple + searchable + tags) so the native input placeholder does not
     * show next to chips. Our previous flex/width rules broke that and duplicated "Select one…".
     */
    ::v-deep .multiselect-wrap--medium .multiselect:not(.multiselect--active) {
      .multiselect__input::placeholder {
        color: transparent;
        opacity: 0;
      }
    }
  }

  .add-guest-btn {
    margin-top: var(--space-smaller);
    padding: var(--space-small) var(--space-normal);
    font-size: var(--font-size-small);
    border: 1px solid var(--s-200);
    border-radius: var(--border-radius-normal);
    background: var(--s-50);
    color: var(--s-700);
    cursor: pointer;
  }

  .guests-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-smaller);
    margin-top: var(--space-small);
  }

  .guest-tag {
    display: inline-flex;
    align-items: center;
    gap: var(--space-micro);
    padding: var(--space-micro) var(--space-smaller);
    background: var(--w-50);
    border: 1px solid var(--w-200);
    border-radius: var(--border-radius-small);
    font-size: var(--font-size-mini);
    color: var(--w-700);
  }

  .remove-guest {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--w-600);
    cursor: pointer;
  }

  .manual-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-small);
    flex-shrink: 0;
  }
}

// Header (title + tabs inline — matches settings/labels/list tab styling)
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-normal) var(--space-medium);
  border-bottom: 1px solid var(--s-75);
  flex-shrink: 0;
  gap: var(--space-normal);

  .modal-header-left {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 12px 20px;
    flex: 1;
    min-width: 0;
  }

  .header-title {
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    color: var(--s-900);
    margin: 0;
    line-height: 1.25;
    padding: 0 0 5px 0;
    flex-shrink: 0;
    /* Mirror tab underline strip so title + tabs share the same vertical rhythm */
    border-bottom: 3px solid transparent;
    box-sizing: border-box;
  }

  .schedule-modal-tabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: baseline;
  }

  .tab-button {
    display: inline-flex;
    align-items: baseline;
    justify-content: center;
    min-width: auto;
    text-align: center;
    padding: 6px 12px 5px 12px;
    margin: 0;
    border: none;
    border-radius: 0;
    /* Reserve underline space on every tab so switching active state does not shift text */
    border-bottom: 3px solid transparent;
    box-sizing: border-box;
    cursor: pointer;
    background-color: transparent;
    color: #151718;
    transition: background-color 0.2s ease, color 0.2s ease,
      border-bottom-color 0.2s ease;
    line-height: 1.25;
    font-size: var(--font-size-small);
    font-weight: 600;
    font-family: inherit;

    &:hover {
      background-color: #f1f3f5;
    }

    &.active {
      background-color: transparent;
      color: #40518a;
      border-bottom-color: #40518a;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: var(--space-small);
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: var(--border-radius-normal);
    color: var(--s-600);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--s-50);
      color: var(--s-900);
    }
  }
}

// Loading State
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-jumbo);
  flex: 1;

  .loading-text {
    margin-top: var(--space-normal);
    color: var(--s-600);
  }
}

// Empty State
.no-events-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-jumbo);
  flex: 1;

  .empty-icon {
    color: var(--s-400);
    margin-bottom: var(--space-normal);
  }

  .empty-text {
    color: var(--s-600);
    margin-bottom: var(--space-normal);
    text-align: center;
  }

  .empty-actions {
    display: flex;
    gap: var(--space-small);
  }
}

// Main Content Layout
.schedule-content {
  display: grid;
  grid-template-columns: 240px 1fr 220px;
  flex: 1;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

// Booking View - Full width 2-panel layout
.booking-view {
  display: grid;
  grid-template-columns: 320px 1fr;
  flex: 1;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}

.booking-summary-panel {
  padding: var(--space-large);
  border-right: 1px solid var(--s-75);
  overflow-y: auto;

  .summary-event-name {
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    color: var(--s-900);
    margin: 0 0 var(--space-normal) 0;
    padding-bottom: var(--space-normal);
    border-bottom: 1px solid var(--s-100);
  }

  .summary-info-card {
    padding: var(--space-normal);
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-normal);
    margin-bottom: var(--space-small);
    background: white;

    .info-row {
      display: flex;
      align-items: center;
      gap: var(--space-smaller);
      color: var(--s-700);
      font-size: var(--font-size-default);
    }

    .info-label {
      font-weight: var(--font-weight-medium);
      color: var(--s-800);
      margin-bottom: var(--space-micro);
    }

    .info-value {
      color: var(--s-600);
      font-size: var(--font-size-small);
    }
  }
}

.platform-selector-loading {
  display: flex;
  align-items: center;
  min-height: 4.4rem;
  padding: var(--space-small);
}

.platform-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-small);

  &--external {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.platform-option {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  background: white;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--w-400);
  }

  &.selected {
    border-color: var(--w-500);
    box-shadow: 0 0 0 1px var(--w-200);
    background: var(--w-50);
  }
}

.platform-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.platform-option__label {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--s-800);
  text-align: left;
}

.booking-form-panel {
  padding: var(--space-large);
  overflow-y: auto;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-normal);
    margin-bottom: var(--space-normal);
  }

  .form-group {
    margin-bottom: var(--space-normal);

    .form-label {
      display: block;
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-medium);
      color: var(--s-800);
      margin-bottom: var(--space-smaller);
    }

    .form-input {
      width: 100%;
      padding: var(--space-small) var(--space-small);
      font-size: var(--font-size-default);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      background: white;
      color: var(--s-900);

      &:focus {
        outline: none;
        border-color: var(--w-500);
        box-shadow: 0 0 0 1px var(--w-200);
      }

      &:disabled {
        background: var(--s-50);
        color: var(--s-600);
        cursor: not-allowed;
      }

      &::placeholder {
        color: var(--s-400);
      }

      &.input-error {
        border-color: var(--r-500);

        &:focus {
          border-color: var(--r-500);
          box-shadow: 0 0 0 1px var(--r-200);
        }
      }
    }

    .error-message {
      display: block;
      margin-top: var(--space-micro);
      font-size: var(--font-size-mini);
      color: var(--r-500);
    }

    .location-option {
      display: inline-flex;
      align-items: center;
      gap: var(--space-smaller);
      padding: var(--space-small) var(--space-normal);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-small);
      color: var(--s-700);
      cursor: pointer;

      &.selected {
        border-color: var(--w-500);
        background: var(--w-25);
        color: var(--w-600);
      }

      .radio-dot {
        width: 16px;
        height: 16px;
        border: 2px solid var(--s-300);
        border-radius: 50%;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: transparent;
        }
      }

      &.selected .radio-dot {
        border-color: var(--w-500);

        &::after {
          background: var(--w-500);
        }
      }
    }

    .add-guest-btn {
      margin-top: var(--space-smaller);
      padding: var(--space-small) var(--space-normal);
      font-size: var(--font-size-small);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      background: var(--s-50);
      color: var(--s-700);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        background: var(--s-100);
        border-color: var(--s-300);
      }
    }

    .guests-list {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-smaller);
      margin-top: var(--space-small);

      .guest-tag {
        display: inline-flex;
        align-items: center;
        gap: var(--space-micro);
        padding: var(--space-micro) var(--space-smaller);
        background: var(--w-50);
        border: 1px solid var(--w-200);
        border-radius: var(--border-radius-small);
        font-size: var(--font-size-mini);
        color: var(--w-700);

        .remove-guest {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: none;
          background: transparent;
          color: var(--w-600);
          cursor: pointer;

          &:hover {
            color: var(--r-500);
          }
        }
      }
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-small);
    padding-top: var(--space-normal);
    border-top: 1px solid var(--s-75);
    margin-top: var(--space-normal);
  }
}

// Left Panel - Event Types
.event-types-panel {
  padding: var(--space-normal);
  border-right: 1px solid var(--s-75);
  overflow-y: auto;
  overflow-x: visible;

  .event-type-card {
    padding: var(--space-normal);
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-normal);
    margin-bottom: var(--space-small);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--w-300);
      background: var(--w-25);
    }

    &.selected {
      border-color: var(--w-500);
      background: var(--w-50);
    }

    .event-name {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      margin: 0 0 var(--space-smaller) 0;
    }

    .event-duration {
      display: inline-flex;
      align-items: center;
      gap: var(--space-micro);
      font-size: var(--font-size-small);
      color: var(--s-600);
      line-height: 1;

      .clock-icon {
        flex-shrink: 0;
        vertical-align: middle;
        color: var(--s-500);
      }

      span {
        line-height: 14px;
      }
    }

    .timezone-section {
      margin-top: var(--space-small);
      padding-top: var(--space-small);
      border-top: 1px solid var(--s-100);
      position: relative;

      .timezone-label {
        display: block;
        font-size: var(--font-size-mini);
        color: var(--s-600);
        margin-bottom: var(--space-micro);
      }

      .timezone-select {
        width: 100%;
        padding: 8px 10px;
        font-size: var(--font-size-mini);
        border: 1px solid var(--s-200);
        border-radius: var(--border-radius-normal);
        background-color: white;
        color: var(--s-800);
        cursor: pointer;
        line-height: 1.3;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-image: none;

        &:focus {
          outline: none;
          border-color: var(--w-500);
          box-shadow: 0 0 0 2px var(--w-100);
        }

        &:hover {
          border-color: var(--s-300);
        }

        option {
          padding: 8px 12px;
          font-size: var(--font-size-small);
          color: var(--s-800);
          background-color: white;
          line-height: 1.5;

          &:checked {
            background-color: var(--w-50);
          }

          &:hover {
            background-color: var(--s-50);
          }
        }
      }
    }
  }
}

// Center Panel - Calendar
.calendar-panel {
  padding: var(--space-normal);
  display: flex;
  flex-direction: column;

  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-normal);

    .month-title {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      margin: 0;
    }

    .nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      border-radius: var(--border-radius-normal);
      color: var(--s-600);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        background: var(--s-50);
        color: var(--s-900);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }

  .calendar-grid {
    .weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
      margin-bottom: var(--space-smaller);

      .weekday {
        text-align: center;
        font-size: var(--font-size-mini);
        font-weight: var(--font-weight-medium);
        color: var(--s-500);
        padding: var(--space-smaller) 0;
      }
    }

    .days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }
  }

  .calendar-day {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: var(--font-size-small);
    cursor: default;
    transition: all 0.15s ease;
    color: var(--s-600);
    width: 32px;
    height: 32px;
    margin: auto;
    background: transparent;

    &.empty {
      cursor: default;
      background: transparent !important;
      border: none !important;
    }

    &.past:not(.empty) {
      color: var(--s-300);
      cursor: not-allowed;
    }

    &.today:not(.selected):not(.empty) {
      border: 1px solid var(--s-400);
      color: var(--s-900);
      font-weight: var(--font-weight-medium);
    }

    &.available:not(.selected):not(.today):not(.empty) {
      background: var(--w-500);
      color: white;
      font-weight: var(--font-weight-medium);
      cursor: pointer;

      &:hover {
        background: var(--w-600);
      }
    }

    &.available.today:not(.selected):not(.empty) {
      background: var(--w-100);
      border-color: var(--w-500);
      color: var(--w-700);
      cursor: pointer;

      &:hover {
        background: var(--w-200);
      }
    }

    &.selected:not(.empty) {
      background: #40518a;
      color: white;
      font-weight: var(--font-weight-bold);
      border: 2px solid #2e3a63;
      box-shadow: 0 0 0 2px rgba(64, 81, 138, 0.3);
    }
  }

  .loading-slots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-small);
    padding: var(--space-normal);
    color: var(--s-600);
    font-size: var(--font-size-small);
  }
}

// Right Panel - Time Slots
.slots-panel {
  padding: var(--space-normal);
  border-left: 1px solid var(--s-75);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .slots-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-normal);
    flex-shrink: 0;

    .selected-date-title {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-bold);
      color: var(--s-900);
      margin: 0;
    }

    .time-format-toggle {
      display: flex;
      border-radius: var(--border-radius-normal);
      overflow: hidden;

      .toggle-btn {
        padding: var(--space-micro) var(--space-small);
        border: 1px solid var(--s-200);
        background: white;
        font-size: var(--font-size-mini);
        cursor: pointer;
        transition: all 0.15s ease;

        &:first-child {
          border-radius: var(--border-radius-normal) 0 0
            var(--border-radius-normal);
          border-right: none;
        }

        &:last-child {
          border-radius: 0 var(--border-radius-normal)
            var(--border-radius-normal) 0;
        }

        &.active {
          background: var(--w-500);
          color: white;
          border-color: var(--w-500);
        }
      }
    }
  }

  .no-date-selected,
  .no-slots {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--s-500);
    font-size: var(--font-size-small);
    text-align: center;
  }

  .slots-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-smaller);
    overflow-y: auto;
    flex: 1;
    padding-right: var(--space-smaller);

    .slot-btn {
      padding: var(--space-small) var(--space-normal);
      border: 1px solid var(--s-200);
      border-radius: var(--border-radius-normal);
      background: white;
      font-size: var(--font-size-small);
      color: var(--s-900);
      cursor: pointer;
      transition: all 0.15s ease;
      text-align: center;

      &:hover {
        border-color: var(--w-500);
        background: var(--w-25);
      }

      &.selected {
        background: var(--w-500);
        color: white;
        border-color: var(--w-500);
      }
    }
  }

  .confirm-section {
    margin-top: var(--space-normal);
    padding-top: var(--space-normal);
    border-top: 1px solid var(--s-75);
    flex-shrink: 0;

    .confirm-btn {
      width: 100%;
    }
  }
}

// Dark Mode
.dark {
  .modal-header {
    .tab-button {
      color: var(--s-300);

      &:hover {
        color: var(--s-100);
        background-color: var(--s-800);
      }

      &.active {
        color: #93a4e8;
        border-bottom-color: #5b6dad;
      }
    }
  }

  .manual-meeting-form {
    .form-label {
      color: var(--s-200);
    }

    .form-input,
    .form-textarea {
      background: var(--s-800);
      border-color: var(--s-600);
      color: var(--s-100);
    }

  }

  .modal-header {
    border-bottom-color: var(--s-700);

    .header-title {
      color: var(--s-100);
    }

    .close-btn {
      color: var(--s-400);

      &:hover {
        background: var(--s-800);
        color: var(--s-100);
      }
    }
  }

  .event-types-panel {
    border-right-color: var(--s-700);

    .event-type-card {
      border-color: var(--s-700);

      &:hover {
        border-color: var(--w-600);
        background: var(--s-800);
      }

      &.selected {
        border-color: var(--w-500);
        background: var(--s-800);
      }

      .event-name {
        color: var(--s-100);
      }

      .timezone-section {
        border-top-color: var(--s-700);

        .timezone-select {
          background: var(--s-800);
          border-color: var(--s-600);
          color: var(--s-100);
        }
      }
    }
  }

  .calendar-panel {
    .calendar-header .month-title {
      color: var(--s-100);
    }

    .calendar-day {
      color: var(--s-500);

      &.available:not(.selected) {
        background: var(--w-900);
        color: var(--w-300);

        &:hover {
          background: var(--w-800);
        }
      }
    }
  }

  .slots-panel {
    border-left-color: var(--s-700);

    .slots-header .selected-date-title {
      color: var(--s-100);
    }

    .time-format-toggle .toggle-btn {
      background: var(--s-800);
      border-color: var(--s-600);
      color: var(--s-300);
    }

    .slots-list .slot-btn {
      background: var(--s-800);
      border-color: var(--s-600);
      color: var(--s-100);

      &:hover {
        border-color: var(--w-500);
      }
    }

    .confirm-section {
      border-top-color: var(--s-700);
    }
  }
}

// Responsive - Medium screens
@media (max-width: 900px) {
  .schedule-content {
    grid-template-columns: minmax(180px, 200px) minmax(250px, 1fr) minmax(
        160px,
        180px
      );
  }

  .event-types-panel .event-type-card {
    padding: var(--space-small);

    .timezone-section .timezone-select {
      font-size: var(--font-size-mini);
    }
  }

  .slots-panel .slots-list .slot-btn {
    padding: var(--space-smaller) var(--space-small);
    font-size: var(--font-size-mini);
  }
}

// Responsive - Small screens
@media (max-width: 700px) {
  .schedule-demo-modal-wrapper {
    ::v-deep .modal-container {
      width: 98%;
      max-width: none;
    }
  }

  .schedule-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
  }

  .event-types-panel {
    border-right: none;
    border-bottom: 1px solid var(--s-75);
    display: flex;
    gap: var(--space-small);
    overflow-x: auto;
    padding: var(--space-small);
    max-height: 120px;

    .event-type-card {
      flex-shrink: 0;
      min-width: 150px;
      margin-bottom: 0;
    }
  }

  .calendar-panel {
    min-height: 300px;
  }

  .slots-panel {
    border-left: none;
    border-top: 1px solid var(--s-75);
    max-height: 200px;

    .slots-list {
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--space-smaller);

      .slot-btn {
        flex: 0 0 auto;
      }
    }
  }
}
</style>
